import type { ComponentType, ReactNode } from "react";
import React from "react";

import { cn } from "@/lib/helpers/cn";
import { mainStyles } from "@/components/layout/Main";
import { useUnreadMessageCount } from "@/features/messaging/api/queries/useUnreadMessageCount";

import { ProfileMenu } from "../ProfileMenu/ProfileMenu";
import { RouteSection } from "../RouteSection/RouteSection";

export type RouteOption = {
  activeIconComponent?: ReactNode;
  icon?: string;
  iconComponent?: ReactNode;
  label: string;
  mobileIcon?: string;
  mobileOnly?: boolean;
  route?: string;
};

export type RouteSectionConfig = {
  options: RouteOption[];
};

export type CustomSection =
  | {
      componentProps?: Record<string, unknown>;
      isComponent: true;
      render: ComponentType<Record<string, unknown>>;
    }
  | { isComponent?: false; render: ReactNode };

type SelfSection = {
  baseRoute?: string;
  profile?: unknown;
};

type DesktopToolbarProps = {
  baseRoute: string;
  customRenderSection?: CustomSection[];
  handleClickSearch: () => void;
  isMessagingEnabled?: boolean;
  isSearchOpen: boolean;
  profile: unknown;
  routeSection?: RouteSectionConfig;
  selfSection?: SelfSection;
};

export function DesktopToolbar({
  baseRoute,
  customRenderSection,
  handleClickSearch,
  isMessagingEnabled = true,
  isSearchOpen,
  profile,
  routeSection,
  selfSection,
}: DesktopToolbarProps) {
  const { data: messageCount = 0 } = useUnreadMessageCount({ enabled: isMessagingEnabled });
  const { container } = mainStyles();

  const renderCustomRenderSection = customRenderSection
    ? customRenderSection.map((section, index) => {
        if (section.isComponent) {
          return (
            <section.render
              key={`section-${index}`}
              messageCount={messageCount}
              {...section.componentProps}
            />
          );
        } else {
          return section.render;
        }
      })
    : [];

  if (selfSection) {
    selfSection["profile"] = profile;
    selfSection["baseRoute"] = baseRoute;
  }

  return (
    <nav
      className={cn(
        "tw:fixed tw:z-201 tw:flex tw:h-(--header-height) tw:w-full tw:justify-center",
        "tw:bg-fill-navy-primary tw:shadow-mdx4",
      )}
    >
      <div className={cn(container(), "tw:flex tw:max-lg:mx-0! tw:max-lg:px-8")}>
        {routeSection && (
          <RouteSection
            config={routeSection}
            handleClickSearch={handleClickSearch}
            isSearchOpen={isSearchOpen}
            messageCount={messageCount}
          />
        )}

        {renderCustomRenderSection}
        {selfSection && (
          <ProfileMenu baseRoute={selfSection.baseRoute} profile={selfSection.profile} />
        )}
      </div>
    </nav>
  );
}
